import React, { useCallback, useEffect, useState } from 'react'
import { t } from '../../../utils/i18n'
import InputField from '../../molecules/input/input-field'
import { ReactComponent as SearchIcon } from '../../../assets/svgs/search-icon.svg'
import NoData from '../../molecules/no-data/no-data'
import { Button } from '../../atoms/button/button'
import { IBusinessAccount } from '../business-account/business-account'
import CustomTable, { TableColumn } from '../../molecules/table/table'
import { useAuth } from '../../../customHooks/useAuth'
import axios from '../../../utils/axios'
import { SUPPLEMENT_LISTS, EDIT_SUPPLEMENT_LISTS } from '../../../constants/routes'
import { showToast } from '../../atoms/toaster/toaster'
import './advertiser.scss'
import { InfoTooltip } from '../../atoms/info-tooltip/info-tooltip'

const RECORDS_PER_PAGE = process.env.REACT_APP_PAGINATION_LIMIT as string
// eslint-disable-next-line
type RowData = Record<string, any>

interface IAdvertiser extends IBusinessAccount {
  selectedAdvertiser: string | null
  sortLatest: string
  setSortLatest?: (val: string) => void
}

export const Advertiser = ({
  isSetModalOpen,
  isBMOnboarded,
  selectedAdvertiser,
  sortLatest,
  setSortLatest,
}: IAdvertiser) => {
  const { user } = useAuth()
  const [supplements, setSupplements] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [limit, setLimit] = useState(Number(RECORDS_PER_PAGE))
  const [totalAdAcc, setTotalAdAcc] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [isSearched, setIsSearched] = useState(false)
  const [isPageLoading, isSetPageLoading] = useState(true)
  const [sortSelected, setSortSelected] = useState('asc')

  useEffect(() => {
    if (sortLatest) {
      setTimeout(() => {
        getSupplementAccounts(sortLatest)
      }, 1000)
    }
  }, [sortLatest])

  useEffect(() => {
    if (!sortLatest) {
      if (selectedAdvertiser?.toLowerCase() === 'all') {
        getSupplementAccounts(sortSelected)
      } else if (selectedAdvertiser) {
        getSupplementAccounts(sortSelected, selectedAdvertiser, 'parentAdvertiserName')
      } else {
        getSupplementAccounts(sortSelected)
      }
    }
  }, [limit, isBMOnboarded, sortSelected, selectedAdvertiser])

  const handleSearch = async (term: string) => {
    setSearchTerm(term)
    getSupplementAccounts('', term)
  }

  const getSupplementAccounts = useCallback(
    async (sort: string, searchTerm?: string, withField?: string) => {
      try {
        setIsLoading(true)
        const config = {
          url:
            searchTerm && !withField
              ? `${SUPPLEMENT_LISTS}?query=${searchTerm}`
              : searchTerm && withField
                ? `${SUPPLEMENT_LISTS}?query=${searchTerm}&field=${withField}&recordsPerPage=${limit}`
                : `${SUPPLEMENT_LISTS}?sort=${sort}&recordsPerPage=${limit}`,
          method: 'GET',
          headers: {
            'x-user': user?.email,
            'x-access-token': user?.tokens?.accessToken?.token,
            'x-source': user?.isSSO ? 'sso' : null,
          },
        }

        const advertiserAccRes = await axios(config)
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        Boolean(searchTerm) && setIsSearched(true)
        setSupplements(advertiserAccRes?.data?.data?.records)
        setTotalAdAcc(advertiserAccRes?.data?.data?.pagination?.totalRecords)
        setIsLoading(false)
      } catch {
        setSupplements([])
        setIsLoading(false)
      } finally {
        setIsLoading(false)
      }
      setSortLatest?.('')
      isSetPageLoading(false)
    },
    [limit]
  )

  const syncKeys: TableColumn[] = [
    {
      key: 'businessAccountId',
      label: t('businessAccountId'),
      width: 181,
      align: 'left',
      clickable: true,
    },
    { key: 'businessName', label: t('businessName'), width: 190, align: 'left' },
    { key: 'adAccountId', label: t('adAccountId'), width: 216, align: 'left', isEditable: true },
    { key: 'adAccountName', label: t('adAccountName'), width: 233, align: 'left' },
    { key: 'FBPageId', label: t('FBPageId'), width: 170, align: 'left', isEditable: true },
    { key: 'FBPageURL', label: t('FBPageURL'), width: 182, align: 'left' },
    { key: 'IGAccountID', label: t('IGAccountID'), width: 191, align: 'left', isEditable: true },
    { key: 'IGAccountName', label: t('IGAccountName'), width: 166, align: 'left' },
    { key: 'datasetID', label: t('datasetID'), width: 170, align: 'left', isEditable: true },
    { key: 'datasetName', label: t('datasetName'), width: 178, align: 'left' },
    { key: 'catalogueID', label: t('catalogueID'), width: 234, align: 'left', isEditable: true },
    { key: 'catalogueName', label: t('catalogueName'), width: 181, align: 'left' },
  ]

  const columns: TableColumn[] = [
    {
      key: 'parentAdvertiserName',
      label: t('parentAdvertiserName'),
      width: 200,
      fixed: true,
      align: 'left',
      sortable: true,
      isEditable: true,
      isRequired: true,
    },
    {
      key: 'brandName',
      label: t('brandName'),
      width: 125,
      fixed: true,
      align: 'left',
      isEditable: true,
      isRequired: true,
    },
    {
      key: 'countryCode',
      label: t('countryCode'),
      width: 100,
      fixed: true,
      align: 'left',
      isEditable: true,
      isRequired: true,
    },
    {
      key: 'vertical',
      label: t('vertical'),
      width: 150,
      fixed: true,
      align: 'left',
      isEditable: true,
      isRequired: true,
    },
    {
      key: 'subVertical',
      label: t('subVertical'),
      width: 200,
      fixed: true,
      align: 'left',
      isEditable: true,
      isRequired: true,
    },
    ...syncKeys.map(({ key, label, align, width, isEditable, clickable }) => ({
      key,
      label,
      width: width || 140,
      fixed: false,
      align,
      clickable,
      isEditable,
    })),
  ]

  function addRowSpanByKeyAndSort(data: RowData[], key: string): RowData[] {
    let i = 0
    while (i < data.length) {
      const currentValue = data[i][key]
      let spanCount = 1

      for (let j = i + 1; j < data.length; j++) {
        if (data[j][key] === currentValue) {
          spanCount++
        } else {
          break
        }
      }

      data[i][`${key}__rowSpan`] = spanCount

      for (let j = 1; j < spanCount; j++) {
        data[i + j][`${key}__rowSpan`] = 0
      }

      i += spanCount
    }

    return data
  }

  const groupedData = addRowSpanByKeyAndSort(supplements, 'parentAdvertiserName')

  const handleEditableChange = async (uniqueId: string, dataKey: string, newValue: string) => {
    interface payloadresp {
      recordSystemId: string
      updates: {
        [x: string]: string
      }
    }

    const payload: payloadresp[] = [
      {
        recordSystemId: uniqueId,
        updates: { [dataKey]: newValue },
      },
    ]
    const editItem = dataKey
    try {
      const config = {
        url: `${EDIT_SUPPLEMENT_LISTS}`,
        method: 'PUT',
        data: payload,
        headers: {
          'x-user': user?.email,
          'x-access-token': user?.tokens?.accessToken?.token,
          'x-source': user?.isSSO ? 'sso' : null,
        },
      }
      const updateSupplement = await axios(config)
      if (updateSupplement?.data?.data?.code === 'RECORD_UPDATED') {
        await getSupplementAccounts(sortSelected)
        showToast({
          message: t('supplement.update.success').replace('{{editItem}}', t(`${editItem}`)),
        })
      }
    } catch {
      await getSupplementAccounts(sortSelected)
      showToast({
        message: t('supplement.update.failed').replace('{{editItem}}', t(`${editItem}`)),
        isError: true,
      })
    }
  }

  return (
    <section className="business-acc-section advertiser-section">
      <div className="top-sec">
        <div className="business-tooltip-container">
          <h1>{t('advertiser.title')}</h1>
          <InfoTooltip
            styleClass="input-info"
            tooltipContent={t('advertiser.title.tooltip')}
            placement={'right'}
          />
        </div>
        {groupedData.length >= 0 && (
          <InputField
            fieldType="text"
            label=""
            value={searchTerm}
            onChange={(s) => handleSearch(s)}
            placeholder={t('advertiser.search.placeholder')}
            icon={<SearchIcon />}
          />
        )}
      </div>
      <div className="bottom-sec">
        {isPageLoading ? (
          <div className="advertiser-pulsating" />
        ) : groupedData.length >= 0 || isSearched ? (
          <CustomTable
            data={groupedData}
            columns={columns}
            isPaginated={false}
            hasLegends={false}
            isLoading={isLoading}
            recordsPerPage={limit}
            total={totalAdAcc}
            isVirtualize={true}
            tableHeight={400}
            autoHeight={false}
            onLimitChange={(limit) => setLimit(limit)}
            columnSorting={setSortSelected}
            handleEditableChange={handleEditableChange}
          />
        ) : (
          <>
            <NoData
              message={''}
              child={
                <div className="no-adv-text-block">
                  <h3>{t('no.advertiser.acc')}</h3>
                  <div className="no-adv-sec-btn">
                    <p>{t('no.advertiser.sec')}</p>
                    <Button
                      label={t('no.advertiser.link')}
                      handleClick={() => isSetModalOpen(true)}
                      removeDefaultCss
                    />
                  </div>
                </div>
              }
            />
          </>
        )}
      </div>
    </section>
  )
}

export default Advertiser
